import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, computed, signal } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { BASE_PATH } from '../../api';
import {
  AsociacionInstitucional,
  CargoPermisos,
  ConcesionPermiso,
  ContextoPermisos,
  EntradaAuditoria,
  PermisoCatalogo
} from '../core/permisos.model';
import { AuthService } from './auth.service';

/**
 * Permisos efectivos de la sesión y administración de permisos (P1.3-104).
 * Los permisos del contexto los calcula el SERVIDOR (cargos vigentes + concesiones + asociaciones institucionales);
 * aquí solo se usan para mostrar u ocultar opciones. La API vuelve a validar cada acción.
 */
@Injectable({ providedIn: 'root' })
export class PermisosService {
  private readonly contextoSignal = signal<ContextoPermisos | null>(null);
  private cargando: Observable<ContextoPermisos | null> | null = null;

  readonly contexto = this.contextoSignal.asReadonly();
  readonly cargado = computed(() => this.contextoSignal() !== null);

  constructor(
    private readonly http: HttpClient,
    private readonly auth: AuthService,
    @Inject(BASE_PATH) private readonly basePath: string
  ) {}

  private headers(): HttpHeaders {
    return new HttpHeaders({ Authorization: 'Bearer ' + this.auth.getToken() });
  }

  private get<T>(path: string, params?: HttpParams): Observable<T> {
    return this.http.get<T>(`${this.basePath}${path}`, { headers: this.headers(), params });
  }

  /** Carga (una vez por sesión) los permisos efectivos. Si falla, el contexto queda vacío: se deniega la UI. */
  cargarContexto(forzar = false): Observable<ContextoPermisos | null> {
    if (!forzar && this.contextoSignal()) return of(this.contextoSignal());
    if (!forzar && this.cargando) return this.cargando;
    const peticion = this.get<{ contexto: ContextoPermisos }>('/permisos/contexto').pipe(
      map(res => res.contexto),
      tap(contexto => { this.contextoSignal.set(contexto); this.cargando = null; }),
      catchError(() => {
        this.cargando = null;
        this.contextoSignal.set({ tipo: 'invalid', id: null, activo: false, global: [], porAsociacion: [] });
        return of(this.contextoSignal());
      })
    );
    this.cargando = peticion;
    return peticion;
  }

  limpiar(): void {
    this.contextoSignal.set(null);
    this.cargando = null;
  }

  /** ¿Tiene el permiso con alcance global? */
  tieneGlobal(permiso: string): boolean {
    return Boolean(this.contextoSignal()?.global.includes(permiso));
  }

  /** ¿Tiene el permiso en alguna asociación concreta (alcance de asociación)? */
  tieneEnAlgunaAsociacion(permiso: string): boolean {
    return Boolean(this.contextoSignal()?.porAsociacion.some(item => item.permisos.includes(permiso)));
  }

  /** ¿Tiene el permiso con alcance global o limitado a ESA asociación? (UX: la API lo vuelve a validar). */
  tieneEnAsociacion(permiso: string, asociacionId: number | null | undefined): boolean {
    if (this.tieneGlobal(permiso)) return true;
    return asociacionId != null && Boolean(this.contextoSignal()?.porAsociacion.some(item => item.asociacionId === asociacionId && item.permisos.includes(permiso)));
  }

  /** Global o limitado a alguna asociación. Sirve para decidir si mostrar una sección. */
  tiene(permiso: string): boolean {
    return this.tieneGlobal(permiso) || this.tieneEnAlgunaAsociacion(permiso);
  }

  tieneAlguno(permisos: string[]): boolean {
    return permisos.some(permiso => this.tiene(permiso));
  }

  // --- API de administración de permisos (requiere permisos:gestionar) ---------------------------------------------

  catalogo(): Observable<PermisoCatalogo[]> {
    return this.get<{ permisos: PermisoCatalogo[] }>('/permisos/catalogo').pipe(map(res => res.permisos));
  }

  /** Cargos elegibles; con `asociacion` (institucional) se incluyen los permisos globales concedidos en ella. */
  cargos(asociacion?: number | null): Observable<{ cargos: CargoPermisos[]; asociacionesInstitucionales: AsociacionInstitucional[]; administradoresVigentes: number | null }> {
    const params = asociacion ? new HttpParams().set('asociacion', String(asociacion)) : undefined;
    return this.get<{ cargos: CargoPermisos[]; asociacionesInstitucionales: AsociacionInstitucional[]; administradoresVigentes: number | null }>('/permisos/cargos', params);
  }

  actualizarCargo(idCargo: number, permisos: ConcesionPermiso[], asociacionId: number | null = null): Observable<{ cargos: CargoPermisos[]; cambios: boolean; anadidos: string[]; retirados: string[] }> {
    return this.http.put<{ cargos: CargoPermisos[]; cambios: boolean; anadidos: string[]; retirados: string[] }>(
      `${this.basePath}/permisos/cargos/${idCargo}`, { asociacionId, permisos }, { headers: this.headers() }
    ).pipe(tap(() => this.cargarContexto(true).subscribe()));
  }

  actualizarInstitucionales(asociaciones: number[]): Observable<AsociacionInstitucional[]> {
    return this.http.put<{ asociaciones: AsociacionInstitucional[] }>(
      `${this.basePath}/permisos/asociaciones-institucionales`, { asociaciones }, { headers: this.headers() }
    ).pipe(map(res => res.asociaciones), tap(() => this.cargarContexto(true).subscribe()));
  }

  auditoria(limite = 50): Observable<EntradaAuditoria[]> {
    return this.get<{ auditoria: EntradaAuditoria[] }>('/permisos/auditoria', new HttpParams().set('limite', String(limite))).pipe(map(res => res.auditoria));
  }
}
